import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

import OnboardingScreen from './screens/auth/OnboardingScreen';
import AboutScreen from './screens/main/AboutScreen';
import CleaningScreen from './screens/main/CleaningScreen';
import HearingTestScreen from './screens/main/HearingTestScreen';
import LearnScreen from './screens/main/LearnScreen';
import HomeScreen from './screens/main/HomeScreen';
import PlansScreen from './screens/main/PlansScreen';
import SettingsScreen from './screens/main/SettingsScreen';

import {
  ARENA_DAYS,
  ARENAS,
  AppProgress,
  AppUser,
  DEFAULT_PROGRESS,
  FREQUENCIES,
  HearingEntry,
  HearingResponse,
  SoundRating,
  TABS,
  TabName,
} from './store/appStore';

import {
  clearUser,
  getProgress,
  getUser,
  isUsernameTaken,
  saveProgress,
  saveUser,
  updateStoredUsername,
} from './store/userStorage';

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function getYesterdayKey() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function deriveArenaFromStreak(streak: number) {
  const arenaCount = ARENAS.length;
  const currentArena = Math.min(Math.floor(streak / ARENA_DAYS) + 1, arenaCount);
  const arenaProgress = ((streak % ARENA_DAYS) / ARENA_DAYS) * 100;
  return { currentArena, arenaProgress };
}

function TabIcon({
  tabName,
  color,
}: {
  tabName: TabName;
  color: string;
}) {
  const size = 20;

  if (tabName === 'home') {
    return <Ionicons name="home-outline" size={size} color={color} />;
  }

  if (tabName === 'hearing') {
    return <Ionicons name="headset-outline" size={size} color={color} />;
  }

  if (tabName === 'cleaning') {
    return <MaterialCommunityIcons name="spray-bottle" size={size} color={color} />;
  }

  if (tabName === 'learn') {
    return <Ionicons name="book-outline" size={size} color={color} />;
  }

  if (tabName === 'about') {
    return <Ionicons name="information-circle-outline" size={size} color={color} />;
  }

  if (tabName === 'account') {
    return <Ionicons name="person-outline" size={size} color={color} />;
  }

  return <Ionicons name="diamond-outline" size={size} color={color} />;
}

export default function App() {
  const [user, setUser] = useState<AppUser | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<TabName>('home');

  const [progress, setProgress] = useState<AppProgress>(DEFAULT_PROGRESS);
  const [showArenaMap, setShowArenaMap] = useState(false);

  const [selectedFrequency, setSelectedFrequency] = useState<number>(FREQUENCIES[0].hz);
  const [currentEar, setCurrentEar] = useState<'left' | 'right'>('left');
  const [isPlayingSound, setIsPlayingSound] = useState(false);
  const [hearingResponses, setHearingResponses] = useState<Record<number, HearingResponse>>({});
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [testComplete, setTestComplete] = useState(false);
  const [hearingScore, setHearingScore] = useState(0);
  const [hearingPercentile, setHearingPercentile] = useState(0);
  const [hearingRank, setHearingRank] = useState('');

  const [cleaningStep, setCleaningStep] = useState(0);

  const summaryFade = useRef(new Animated.Value(0)).current;
  const summaryRise = useRef(new Animated.Value(18)).current;
  const cleaningFade = useRef(new Animated.Value(1)).current;
  const statBars = useRef(FREQUENCIES.map(() => new Animated.Value(0))).current;

  useEffect(() => {
    const load = async () => {
      const existingUser = await getUser();

      if (existingUser) {
        setUser(existingUser);
        const storedProgress = await getProgress(existingUser.username);

        const yesterday = getYesterdayKey();
        let nextProgress = storedProgress;

        if (
          nextProgress.lastCompletedDate &&
          nextProgress.lastCompletedDate !== getTodayKey() &&
          nextProgress.lastCompletedDate !== yesterday &&
          nextProgress.streak > 0
        ) {
          nextProgress = {
            ...nextProgress,
            streak: 0,
          };
          await saveProgress(existingUser.username, nextProgress);
        }

        setProgress(nextProgress);
      }

      setBootLoading(false);
    };

    load();
  }, []);

  const { currentArena, arenaProgress } = useMemo(() => {
    return deriveArenaFromStreak(progress.streak);
  }, [progress.streak]);

  const todayKey = getTodayKey();
  const daysSinceLastClean: number | null = progress.lastCleanDate
    ? Math.floor(
        (new Date(todayKey).getTime() - new Date(progress.lastCleanDate).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : null;
  const todayStatus = progress.dailyStatus[todayKey] || {
    hearingDone: false,
    cleaningDone: false,
    streakAwarded: false,
    learnQuizDone: false,
  };

  const completedFrequencyCount = useMemo(() => {
    return Object.values(hearingResponses).filter(
      (response) => response.left || response.right
    ).length;
  }, [hearingResponses]);

  const playSound = () => {
    if (isPlayingSound) return;
    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const duration = 1.5;
      const fadeTime = 0.05;

      const oscillator = ctx.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(selectedFrequency, ctx.currentTime);

      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.4, ctx.currentTime + fadeTime);
      gainNode.gain.setValueAtTime(0.4, ctx.currentTime + duration - fadeTime);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);

      const panner = ctx.createStereoPanner();
      panner.pan.setValueAtTime(currentEar === 'left' ? -1 : 1, ctx.currentTime);

      oscillator.connect(gainNode);
      gainNode.connect(panner);
      panner.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
      setIsPlayingSound(true);
      oscillator.onended = () => setIsPlayingSound(false);
    } catch (e) {
      console.warn('Web Audio API not available:', e);
      setIsPlayingSound(true);
      setTimeout(() => setIsPlayingSound(false), 1500);
    }
  };

  const animateResults = () => {
    summaryFade.setValue(0);
    summaryRise.setValue(18);
    statBars.forEach((bar) => bar.setValue(0));

    Animated.parallel([
      Animated.timing(summaryFade, {
        toValue: 1,
        duration: 450,
        useNativeDriver: true,
      }),
      Animated.timing(summaryRise, {
        toValue: 0,
        duration: 450,
        useNativeDriver: true,
      }),
    ]).start();

    Animated.stagger(
      55,
      statBars.map((bar) =>
        Animated.timing(bar, {
          toValue: 1,
          duration: 420,
          useNativeDriver: false,
        })
      )
    ).start();
  };

  const awardDailyStreakIfEligible = async (incomingProgress: AppProgress) => {
    if (!user) return;

    const today = getTodayKey();
    const yesterday = getYesterdayKey();

    const todayEntry = incomingProgress.dailyStatus[today] || {
      hearingDone: false,
      cleaningDone: false,
      streakAwarded: false,
      learnQuizDone: false,
    };

    let nextProgress = { ...incomingProgress };

    // Streak gate: hearing test + learn quiz must both be done.
    // Cleaning is a weekly habit — it contributes to totalCleanings but
    // is not required daily so it does not block the streak.
    if (todayEntry.hearingDone && todayEntry.learnQuizDone && !todayEntry.streakAwarded) {
      let nextStreak = 1;

      if (nextProgress.lastCompletedDate === yesterday) {
        nextStreak = nextProgress.streak + 1;
      } else if (nextProgress.lastCompletedDate === today) {
        nextStreak = nextProgress.streak;
      }

      nextProgress = {
        ...nextProgress,
        streak: nextStreak,
        lastCompletedDate: today,
        dailyStatus: {
          ...nextProgress.dailyStatus,
          [today]: {
            ...todayEntry,
            streakAwarded: true,
          },
        },
      };
    }

    setProgress(nextProgress);
    await saveProgress(user.username, nextProgress);
  };

  const markHearingCompleteForToday = async () => {
    if (!user) return;

    const today = getTodayKey();
    const current = await getProgress(user.username);

    const todayEntry = current.dailyStatus[today] || {
      hearingDone: false,
      cleaningDone: false,
      streakAwarded: false,
      learnQuizDone: false,
    };

    const nextProgress: AppProgress = {
      ...current,
      totalHearingTests: todayEntry.hearingDone
        ? current.totalHearingTests
        : current.totalHearingTests + 1,
      dailyStatus: {
        ...current.dailyStatus,
        [today]: {
          ...todayEntry,
          hearingDone: true,
        },
      },
    };

    await awardDailyStreakIfEligible(nextProgress);
  };

  const markCleaningCompleteForToday = async () => {
    if (!user) return;

    const today = getTodayKey();
    const current = await getProgress(user.username);

    const todayEntry = current.dailyStatus[today] || {
      hearingDone: false,
      cleaningDone: false,
      streakAwarded: false,
      learnQuizDone: false,
    };

    const nextProgress: AppProgress = {
      ...current,
      totalCleanings: todayEntry.cleaningDone
        ? current.totalCleanings
        : current.totalCleanings + 1,
      dailyStatus: {
        ...current.dailyStatus,
        [today]: {
          ...todayEntry,
          cleaningDone: true,
        },
      },
    };

    await awardDailyStreakIfEligible(nextProgress);
  };

  const completeHearingTest = async (
    updatedResponses: Record<number, HearingResponse>
  ) => {
    let weightedEarned = 0;
    let weightedTotal = 0;

    FREQUENCIES.forEach((freq) => {
      const response = updatedResponses[freq.hz];
      const heardCount = (response?.left ? 1 : 0) + (response?.right ? 1 : 0);
      const normalizedScore = heardCount / 2;

      weightedEarned += normalizedScore * freq.weight;
      weightedTotal += freq.weight;
    });

    const score = Math.round((weightedEarned / weightedTotal) * 100);
    const percentile = Math.round((weightedEarned / weightedTotal) * 100);

    const rank =
      score >= 92 ? 'Exceptional Range' :
      score >= 82 ? 'Very Strong' :
      score >= 70 ? 'Above Average' :
      score >= 55 ? 'Moderate Range' :
      score >= 40 ? 'Limited High-End Range' :
      'Needs Improvement';

    setHearingScore(score);
    setHearingPercentile(percentile);
    setHearingRank(rank);

    // Build per-frequency result map for history
    const frequencyResults: Record<number, number> = {};
    FREQUENCIES.forEach((freq) => {
      const response = updatedResponses[freq.hz];
      const heardCount = (response?.left ? 1 : 0) + (response?.right ? 1 : 0);
      frequencyResults[freq.hz] = (heardCount / 2) * 100;
    });

    // Save entry to hearing history
    const entry: HearingEntry = {
      date: getTodayKey(),
      score,
      percentile,
      rank,
      frequencyResults,
    };

    if (user) {
      const current = await getProgress(user.username);
      // Replace any existing entry for today to prevent duplicate same-day saves
      const filtered = (current.hearingHistory || []).filter((e) => e.date !== getTodayKey());
      const nextProgress = {
        ...current,
        hearingHistory: [...filtered, entry],
      };
      await saveProgress(user.username, nextProgress);
      setProgress(nextProgress);
    }

    setTestComplete(true);
    animateResults();
    await markHearingCompleteForToday();
  };

  const handleHearingResponse = (heard: boolean) => {
    const updatedResponses: Record<number, HearingResponse> = {
      ...hearingResponses,
      [selectedFrequency]: {
        ...(hearingResponses[selectedFrequency] || { left: false, right: false }),
        [currentEar]: heard,
      },
    };

    setHearingResponses(updatedResponses);

    if (currentEar === 'left') {
      setCurrentEar('right');
      return;
    }

    const nextFreqIndex = FREQUENCIES.findIndex((f) => f.hz === selectedFrequency);

    if (nextFreqIndex < FREQUENCIES.length - 1) {
      setSelectedFrequency(FREQUENCIES[nextFreqIndex + 1].hz);
      setCurrentEar('left');
    } else {
      void completeHearingTest(updatedResponses);
    }
  };

  const resetHearingTest = () => {
    setTestComplete(false);
    setHearingResponses({});
    setSelectedFrequency(FREQUENCIES[0].hz);
    setCurrentEar('left');
    setHearingScore(0);
    setHearingPercentile(0);
    setHearingRank('');
    summaryFade.setValue(0);
    summaryRise.setValue(18);
    statBars.forEach((bar) => bar.setValue(0));
  };

  const animateToCleaningStep = (nextStep: number) => {
    Animated.timing(cleaningFade, {
      toValue: 0,
      duration: 180,
      useNativeDriver: true,
    }).start(() => {
      setCleaningStep(nextStep);
      Animated.timing(cleaningFade, {
        toValue: 1,
        duration: 220,
        useNativeDriver: true,
      }).start();
    });
  };

  const nextCleaningStep = () => {
    if (cleaningStep < 8) {
      animateToCleaningStep(cleaningStep + 1);
    }
  };

  const prevCleaningStep = () => {
    if (cleaningStep > 0) {
      animateToCleaningStep(cleaningStep - 1);
    }
  };

  const restartCleaning = () => {
    animateToCleaningStep(0);
  };

  const handleFinishCleaning = async (beforeRating: number, afterRating: number) => {
    await markCleaningCompleteForToday();

    // Save sound rating + lastCleanDate
    if (user) {
      const current = await getProgress(user.username);
      const entry: SoundRating = {
        date: getTodayKey(),
        before: beforeRating,
        after: afterRating,
        delta: afterRating - beforeRating,
      };
      const nextProgress = {
        ...current,
        lastCleanDate: getTodayKey(),
        soundRatingHistory: [...(current.soundRatingHistory || []), entry],
      };
      setProgress(nextProgress);
      await saveProgress(user.username, nextProgress);
    }

    setCurrentTab('home');
  };

  const handleOnboardingComplete = async (
    incomingUser: AppUser,
    options?: { isLogin?: boolean }
  ) => {
    if (options?.isLogin) {
      const loadedProgress = await getProgress(incomingUser.username);
      setUser(incomingUser);
      setProgress(loadedProgress);
      setCurrentTab('home');
      setShowArenaMap(false);
      setCleaningStep(0);
      setTestComplete(false);
      setHearingResponses({});
      setSelectedFrequency(FREQUENCIES[0].hz);
      setCurrentEar('left');
      setHearingScore(0);
      setHearingPercentile(0);
      setHearingRank('');
      return;
    }

    await saveUser(incomingUser);
    const loadedProgress = await getProgress(incomingUser.username);

    setUser(incomingUser);
    setProgress(loadedProgress);
    setCurrentTab('home');
  };

  const saveUsername = async (
    nextName: string
  ): Promise<{ success: boolean; message?: string }> => {
    if (!user) {
      return { success: false, message: 'No user found.' };
    }

    const trimmed = nextName.trim();

    if (!trimmed) {
      return { success: false, message: 'Please enter a username.' };
    }

    if (!/^[a-zA-Z0-9_]{3,16}$/.test(trimmed)) {
      return {
        success: false,
        message:
          'Username must be 3 to 16 characters and use only letters, numbers, or underscores.',
      };
    }

    if (trimmed.toLowerCase() !== user.username.toLowerCase()) {
      const taken = await isUsernameTaken(trimmed);

      if (taken) {
        return { success: false, message: 'That username is already taken.' };
      }

      await updateStoredUsername(user.username, trimmed);
    }

    const updated = { ...user, username: trimmed };
    setUser(updated);
    await saveUser(updated);

    const loadedProgress = await getProgress(trimmed);
    setProgress(loadedProgress);

    return { success: true };
  };

  const handleQuizPass = async () => {
    if (!user) return;
    const today = getTodayKey();
    const current = await getProgress(user.username);
    const todayEntry = current.dailyStatus[today] || {
      hearingDone: false,
      cleaningDone: false,
      streakAwarded: false,
      learnQuizDone: false,
    };
    if (todayEntry.learnQuizDone) return; // already awarded today
    const nextProgress: AppProgress = {
      ...current,
      learnPoints: (current.learnPoints || 0) + 10,
      dailyStatus: {
        ...current.dailyStatus,
        [today]: { ...todayEntry, learnQuizDone: true },
      },
    };
    // Also check if hearing is already done — if so, award the streak now
    await awardDailyStreakIfEligible(nextProgress);
  };

  const handleLogout = async () => {
    await clearUser();
    setUser(null);
    setProgress(DEFAULT_PROGRESS);
    setCurrentTab('home');
    setShowArenaMap(false);
    setCleaningStep(0);
    setTestComplete(false);
    setHearingResponses({});
    setSelectedFrequency(FREQUENCIES[0].hz);
    setCurrentEar('left');
    setHearingScore(0);
    setHearingPercentile(0);
    setHearingRank('');
  };

  if (bootLoading) {
    return <View style={{ flex: 1, backgroundColor: '#0f0f1e' }} />;
  }

  if (!user) {
    return <OnboardingScreen onComplete={handleOnboardingComplete} />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: '#0f0f1e' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingTop: 24, paddingBottom: 120 }}
        showsVerticalScrollIndicator={false}
      >
        {currentTab === 'home' && (
          <HomeScreen
            streak={progress.streak}
            currentArena={currentArena}
            arenaProgress={arenaProgress}
            showArenaMap={showArenaMap}
            setShowArenaMap={setShowArenaMap}
            todayHearingDone={todayStatus.hearingDone}
            todayCleaningDone={todayStatus.cleaningDone}
            todayLearnDone={todayStatus.learnQuizDone ?? false}
            hearingHistory={progress.hearingHistory || []}
            totalCleanings={progress.totalCleanings}
            learnPoints={progress.learnPoints || 0}
          />
        )}

        {currentTab === 'hearing' && (
          <HearingTestScreen
            selectedFrequency={selectedFrequency}
            currentEar={currentEar}
            isPlayingSound={isPlayingSound}
            completedFrequencyCount={completedFrequencyCount}
            frequencies={FREQUENCIES}
            hearingResponses={hearingResponses}
            testComplete={testComplete}
            hearingScore={hearingScore}
            hearingPercentile={hearingPercentile}
            hearingRank={hearingRank}
            summaryFade={summaryFade}
            summaryRise={summaryRise}
            statBars={statBars}
            hearingHistory={progress.hearingHistory || []}
            playSound={playSound}
            handleHearingResponse={handleHearingResponse}
            resetHearingTest={resetHearingTest}
          />
        )}

        {currentTab === 'cleaning' && (
          <CleaningScreen
            cleaningStep={cleaningStep}
            cleaningFade={cleaningFade}
            nextCleaningStep={nextCleaningStep}
            prevCleaningStep={prevCleaningStep}
            restartCleaning={restartCleaning}
            onFinishCleaning={handleFinishCleaning}
            totalCleanings={progress.totalCleanings}
            daysSinceLastClean={daysSinceLastClean}
          />
        )}

        {currentTab === 'learn' && (
          <LearnScreen
            quizDoneToday={todayStatus.learnQuizDone ?? false}
            learnPoints={progress.learnPoints || 0}
            onQuizPass={handleQuizPass}
          />
        )}

        {currentTab === 'about' && <AboutScreen />}

        {currentTab === 'account' && (
          <SettingsScreen
            user={user}
            progress={progress}
            onSaveUsername={saveUsername}
            onLogout={handleLogout}
          />
        )}

        {currentTab === 'plans' && <PlansScreen />}
      </ScrollView>

      <View
        style={{
          flexDirection: 'row',
          backgroundColor: '#1a1a2e',
          borderTopWidth: 2,
          borderTopColor: '#00ff00',
          paddingTop: 12,
          paddingBottom: 20,
        }}
      >
        {TABS.map((tab) => {
          const active = currentTab === tab.name;
          const color = active ? '#00ff00' : '#888888';

          return (
            <TouchableOpacity
              key={tab.name}
              onPress={() => setCurrentTab(tab.name)}
              style={{ flex: 1, alignItems: 'center', paddingVertical: 10 }}
            >
              <TabIcon tabName={tab.name} color={color} />
              <Text
                style={{
                  fontSize: 10,
                  color,
                  fontWeight: '700',
                  marginTop: 4,
                }}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}